import { ref } from 'vue';

// Global navigation progress state — a single thin top-bar for the whole app.
// Driven by the router hooks in src/router/index.js: startNavigationProgress() on
// beforeEach, finishNavigationProgress() on afterEach / onError. The bar exists so a
// slow "cold" route (a page whose chunk is still loading) reads as "loading", not "hung".
const isActive = ref(false);
const progress = ref(0);

// Don't flash the bar for fast (cached / warm) navigations — only show once a navigation
// has been pending longer than this.
const SHOW_DELAY = 160;
// Coalesce a redirect chain / rapid successive navigations into one continuous bar: a
// finish is deferred briefly, and a new start within this window cancels it.
const FINISH_DEBOUNCE = 80;
const HIDE_DELAY = 240;
const TRICKLE_INTERVAL = 240;

let showTimer = null;
let trickleTimer = null;
let finishTimer = null;
let hideTimer = null;

const stopTrickle = () => {
  if (trickleTimer) {
    clearInterval(trickleTimer);
    trickleTimer = null;
  }
};

const startTrickle = () => {
  stopTrickle();
  trickleTimer = setInterval(() => {
    // Ease toward 0.9 without ever reaching it — real completion happens in finish().
    if (progress.value < 0.9) progress.value += (0.9 - progress.value) * 0.12;
  }, TRICKLE_INTERVAL);
};

// Called when a navigation begins.
export const startNavigationProgress = () => {
  // A new navigation cancels a pending finish (redirect chain / rapid clicks) so the bar
  // stays continuous instead of blinking.
  if (finishTimer) {
    clearTimeout(finishTimer);
    finishTimer = null;
  }
  if (hideTimer) {
    clearTimeout(hideTimer);
    hideTimer = null;
  }
  if (isActive.value || showTimer) return; // already running or arming

  progress.value = 0;
  showTimer = setTimeout(() => {
    showTimer = null;
    isActive.value = true;
    progress.value = 0.08;
    startTrickle();
  }, SHOW_DELAY);
};

// Called when a navigation settles (confirmed, aborted, redirected or errored).
export const finishNavigationProgress = () => {
  if (finishTimer) clearTimeout(finishTimer);
  finishTimer = setTimeout(() => {
    finishTimer = null;
    if (showTimer) {
      // Navigation finished before the show threshold — it never flashed.
      clearTimeout(showTimer);
      showTimer = null;
      progress.value = 0;
      return;
    }
    if (!isActive.value) return;
    stopTrickle();
    progress.value = 1;
    hideTimer = setTimeout(() => {
      hideTimer = null;
      isActive.value = false;
      progress.value = 0;
    }, HIDE_DELAY);
  }, FINISH_DEBOUNCE);
};

export const useNavigationProgress = () => ({ isActive, progress });
